import { useEffect, useState } from "react";
import { usePacks } from "../../../components/packs/PackContext";
import type { PackMode } from "../../../store/api/packsApi";

/**
 * Header brand logo — swaps with the CRM/Advisor view mode (usePacks().
 * currentMode), driven by the same in-app state the ModeToggle flips, so the
 * switch is instant (no reload/flicker).
 *
 * Both logos are white horizontal wordmarks (the LIGHT variant) for the fixed
 * dark header (#242424) — same approach the Advisor logo always used; the _Dark
 * variants exist alongside but are unused here.
 *
 * Loading: until the installed-packs query resolves the mode (usePacks().
 * isLoading), we render NEITHER logo — just a skeleton sized to the logo
 * footprint — so we never flash the wrong (default Advisor) logo before CRM is
 * known. Both logo sources are preloaded (hidden) so the resolved one — and a
 * later toggle — paints instantly from cache.
 *
 * Same render height in both modes (className, default h-[36px]) so the header
 * never shifts on toggle.
 */
const LOGO_BY_MODE: Record<PackMode, { src: string; alt: string }> = {
  // %20 — the CRM white asset filename contains a space ("skyCRM _Light.svg").
  operator_crm: { src: "/dist/img/skyCRM%20_Light.svg", alt: "SkyCRM" },
  lead_gen_only: { src: "/dist/img/skyAdvisor_Light.svg", alt: "SkyAdvisor" },
};
const ALL_LOGO_SRCS = Object.values(LOGO_BY_MODE).map((l) => l.src);

export function TenantLogo({ className = "h-[36px]" }: { className?: string }) {
  const { currentMode, isLoading } = usePacks();
  const logo = LOGO_BY_MODE[currentMode] ?? LOGO_BY_MODE.lead_gen_only;
  const [imageError, setImageError] = useState(false);

  // Clear any prior load error when the mode (and thus the src) changes, so a
  // one-off failure in one mode doesn't strand the text fallback in the other.
  useEffect(() => setImageError(false), [logo.src]);

  return (
    <div className="flex items-center">
      {/* Preload BOTH logos (hidden) so the resolved mode — and any later
          toggle — paints instantly from cache, no flash. */}
      <div
        aria-hidden
        className="hidden"
      >
        {ALL_LOGO_SRCS.map((src) => (
          <img
            key={src}
            src={src}
            alt=""
          />
        ))}
      </div>

      {isLoading ? (
        // Mode not yet resolved — reserve the logo footprint with a subtle
        // skeleton so the header never shifts and the wrong logo never flashes.
        <div className={`${className} w-[120px] animate-pulse rounded bg-white/10`} />
      ) : imageError ? (
        <span className="text-white text-lg font-semibold">{logo.alt}</span>
      ) : (
        <img
          className={className}
          src={logo.src}
          alt={logo.alt}
          onError={() => setImageError(true)}
        />
      )}
    </div>
  );
}
